import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { FileDropzone } from '../components/admin/FileDropzone';
import { ChapterPreviewTable } from '../components/admin/ChapterPreviewTable';
import { Loading } from '../components/Loading';
import type { ParsedChapter } from '../services/types';

type Stage = 'upload' | 'preview' | 'saving';

interface FileContent {
  filename: string;
  content: string;
}

interface UploadProgress {
  current: number;
  total: number;
  currentChapterTitle: string;
  phase: 'creating' | 'uploading' | 'completing';
}

export function AdminNovelUploadPage() {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>('upload');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const abortRef = useRef(false);

  // 檔案內容快取
  const [fileContents, setFileContents] = useState<FileContent[]>([]);

  // 解析結果
  const [detectedTitle, setDetectedTitle] = useState<string | null>(null);
  const [chapters, setChapters] = useState<ParsedChapter[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);

  // 小說資訊
  const [novelTitle, setNovelTitle] = useState('');
  const [novelAuthor, setNovelAuthor] = useState('');
  const [novelDescription, setNovelDescription] = useState('');

  // 處理檔案上傳
  const handleFilesReady = useCallback(async (files: File[]) => {
    setIsLoading(true);
    setError(null);

    try {
      // 讀取所有檔案內容
      const contents: FileContent[] = [];
      for (const file of files) {
        const content = await file.text();
        contents.push({
          filename: file.name,
          content
        });
      }
      setFileContents(contents);

      // 呼叫 API 解析
      const result = await api.parseNovelFiles(files);

      setDetectedTitle(result.detectedTitle);
      setChapters(result.chapters);
      setWarnings(result.warnings);

      // 預設使用偵測到的書名
      if (result.detectedTitle) {
        setNovelTitle(result.detectedTitle);
      }

      setStage('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : '解析檔案時發生錯誤');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 更新章節順序
  const handleReorder = useCallback((newChapters: ParsedChapter[]) => {
    setChapters(newChapters);
  }, []);

  // 更新章節標題
  const handleTitleChange = useCallback((index: number, title: string) => {
    setChapters(prev => {
      const newChapters = [...prev];
      newChapters[index] = { ...newChapters[index], suggestedTitle: title };
      return newChapters;
    });
  }, []);

  // 移除章節
  const handleRemove = useCallback((index: number) => {
    setChapters(prev => {
      const newChapters = prev.filter((_, i) => i !== index);
      // 重新編號
      newChapters.forEach((ch, i) => {
        ch.suggestedIndex = i + 1;
      });
      return newChapters;
    });
  }, []);

  // 重新上傳
  const handleReset = useCallback(() => {
    setStage('upload');
    setFileContents([]);
    setChapters([]);
    setWarnings([]);
    setDetectedTitle(null);
    setNovelTitle('');
    setNovelAuthor('');
    setNovelDescription('');
    setError(null);
    setUploadProgress(null);
    abortRef.current = false;
  }, []);

  // 確認上傳 (分批上傳)
  const handleUpload = useCallback(async () => {
    if (!novelTitle.trim()) {
      setError('請輸入小說標題');
      return;
    }

    if (chapters.length === 0) {
      setError('請至少保留一個章節');
      return;
    }

    setStage('saving');
    setIsLoading(true);
    setError(null);
    abortRef.current = false;

    try {
      // Phase 1: 建立小說 (不含章節)
      setUploadProgress({
        current: 0,
        total: chapters.length,
        currentChapterTitle: '',
        phase: 'creating'
      });

      const novel = await api.createNovelOnly({
        title: novelTitle.trim(),
        author: novelAuthor.trim() || undefined,
        description: novelDescription.trim() || undefined,
        totalChapters: chapters.length
      });

      // Phase 2: 逐章上傳
      for (let i = 0; i < chapters.length; i++) {
        if (abortRef.current) {
          throw new Error('上傳已取消');
        }

        const chapter = chapters[i];
        const fileContent = fileContents.find(
          fc => fc.filename === chapter.originalFilename
        );

        setUploadProgress({
          current: i + 1,
          total: chapters.length,
          currentChapterTitle: chapter.suggestedTitle,
          phase: 'uploading'
        });

        await api.uploadChapter(novel.id, {
          index: i + 1,
          title: chapter.suggestedTitle,
          content: fileContent?.content || ''
        });
      }

      // Phase 3: 完成上傳
      setUploadProgress({
        current: chapters.length,
        total: chapters.length,
        currentChapterTitle: '',
        phase: 'completing'
      });

      await api.completeNovelUpload(novel.id);

      // 成功，導向小說詳情頁
      navigate(`/novels/${novel.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : '上傳失敗');
      setStage('preview');
    } finally {
      setIsLoading(false);
      setUploadProgress(null);
    }
  }, [novelTitle, novelAuthor, novelDescription, chapters, fileContents, navigate]);

  // 取消上傳
  const handleCancelUpload = useCallback(() => {
    abortRef.current = true;
  }, []);

  // 計算總字數
  const totalWordCount = chapters.reduce((sum, ch) => sum + ch.wordCount, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">上傳小說</h1>
        {stage !== 'upload' && (
          <button
            onClick={handleReset}
            className="text-sm text-gray-600 hover:text-gray-800"
            disabled={isLoading}
          >
            重新上傳
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {stage === 'upload' && (
        <FileDropzone onFilesReady={handleFilesReady} isLoading={isLoading} />
      )}

      {stage === 'saving' && uploadProgress && (
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <h2 className="text-lg font-medium">
            {uploadProgress.phase === 'creating' && '正在建立小說...'}
            {uploadProgress.phase === 'uploading' && '正在上傳章節...'}
            {uploadProgress.phase === 'completing' && '正在完成上傳...'}
          </h2>

          {/* 進度條 */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span>
                {uploadProgress.phase === 'uploading'
                  ? `第 ${uploadProgress.current} / ${uploadProgress.total} 章`
                  : uploadProgress.phase === 'creating'
                    ? '準備中...'
                    : '完成中...'
                }
              </span>
              <span>
                {Math.round((uploadProgress.current / uploadProgress.total) * 100)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className="bg-primary-600 h-full transition-all duration-300 ease-out"
                style={{
                  width: `${(uploadProgress.current / uploadProgress.total) * 100}%`
                }}
              />
            </div>
          </div>

          {/* 目前章節 */}
          {uploadProgress.phase === 'uploading' && uploadProgress.currentChapterTitle && (
            <p className="text-sm text-gray-500 truncate">
              正在上傳: {uploadProgress.currentChapterTitle}
            </p>
          )}

          {/* 取消按鈕 */}
          <button
            onClick={handleCancelUpload}
            className="text-sm text-red-600 hover:text-red-700"
          >
            取消上傳
          </button>
        </div>
      )}

      {stage === 'preview' && !isLoading && (
        <div className="space-y-6">
          {/* 小說資訊表單 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium mb-4">小說資訊</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  標題 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={novelTitle}
                  onChange={(e) => setNovelTitle(e.target.value)}
                  placeholder={detectedTitle || '輸入小說標題'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                {detectedTitle && detectedTitle !== novelTitle && (
                  <p className="text-xs text-gray-500 mt-1">
                    偵測到的標題: {detectedTitle}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  作者
                </label>
                <input
                  type="text"
                  value={novelAuthor}
                  onChange={(e) => setNovelAuthor(e.target.value)}
                  placeholder="輸入作者名稱 (選填)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  簡介
                </label>
                <textarea
                  value={novelDescription}
                  onChange={(e) => setNovelDescription(e.target.value)}
                  placeholder="輸入小說簡介 (選填)"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                />
              </div>
            </div>
          </div>

          {/* 警告訊息 */}
          {warnings.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded">
              <div className="font-medium mb-1">解析警告:</div>
              <ul className="list-disc list-inside text-sm">
                {warnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            </div>
          )}

          {/* 章節預覽 */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-medium">
                章節預覽 ({chapters.length} 章)
              </h2>
              <span className="text-sm text-gray-500">
                總字數: {totalWordCount.toLocaleString()} 字
              </span>
            </div>
            <ChapterPreviewTable
              chapters={chapters}
              onReorder={handleReorder}
              onTitleChange={handleTitleChange}
              onRemove={handleRemove}
            />
          </div>

          {/* 操作按鈕 */}
          <div className="flex justify-end gap-4">
            <button
              onClick={handleReset}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              取消
            </button>
            <button
              onClick={handleUpload}
              disabled={!novelTitle.trim() || chapters.length === 0}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              確認上傳 ({chapters.length} 章)
            </button>
          </div>
        </div>
      )}

      {isLoading && stage === 'upload' && (
        <Loading text="正在解析檔案..." />
      )}
    </div>
  );
}
